import {
  BOTTOM_BOUNDARY6,
  BOTTOM_BOUNDARY8,
  DOUBLE_POINTS,
  TOP_BOUNDARY,
  GAME_BOARD_SIZE,
  DIRECTIONS,
  HEX_LINE_WIDTH,
  P1_REMOVES,
  P2_REMOVES,
} from "../constants_v2.js";
import { gameState } from "./gameBoard_v2.js"; // pretty much all these uilities are about gameState, so this makes sense
import { zoom } from "../zoom_v2.js";
import { colors } from "../colors_v2.js";
import { matchService } from "../matches/matchService_v2.js";

// types:
/**
 * @typedef {object} piece
 * @property {string} player - "playerOne", "playerTwo", or "Empty"
 * @property {string} direction - "up", "upRight", "upLeft", "down", "downRight", "downLeft", or "Empty"
 * @property {coords} coords - {s, r, q} i.e. an object with all three game coordinate axes
 */

/**
 * @typedef {object} coords
 * @property {string} s - The s axis (downLeft through upRight )
 * @property {string} q - The q axis (vertical)
 * @property {string} r - The r axis ( upLeft through downRight )
 * ```
 *    +s   +r
 * -q   Hex  +q
 *    -s   +s
 * ```
 * * s axis (upLeft through downRight, increments up left, decrements down right)
 * * q axis (horizontal axis, 0 is the left most, increments to the right)
 * * r axis (downLeft through upRight, decrements up right, increments down left)
 */

export const calculateFillColor = (coords) => {
  const { player } = gameCoordState(coords);
  switch (player) {
    case "playerTwo":
      return colors.getPlayerColor({ player: "playerTwo", property: "piece" });
    case "playerOne":
      return colors.getPlayerColor({ player: "playerOne", property: "piece" });
    case "Empty":
      return colors.getGameColor("background");
  }
};

export const calculateBorderColor = (coords) => {
  const available = coordInAvailables(coords);
  switch (true) {
    case available === true:
      return colors.getGameColor("hex_available");
    default:
      return colors.getGameColor("background");
  }
};

export const hoverColor = (available) => {
  let color;
  switch (available) {
    case true:
      color = colors.getPlayerColor({
        player: matchService.getTurn().player,
        property: "hover",
      });
      break;
    case false:
      color = colors.getPlayerColor({
        player: matchService.getTurn().player,
        property: "piece",
      });
      break;
  }
  return color;
};

/**
 *
 * Tells you if a set of coordinates match gameState.hoveringHexagon
 * (i.e. the hexagon that the mouse is hovering over)
 * @param {coords} coords
 * @returns {boolean} true or false
 */
export const isHoveringHexagon = (coords) => {
  if (
    gameState.hoveringHexagon.s === coords.s &&
    gameState.hoveringHexagon.q === coords.q &&
    gameState.hoveringHexagon.r === coords.r
  ) {
    return true;
  } else {
    return false;
  }
};

export const isDouble = (coords) => {
  const index = coordInPieces({ coords, piecesArray: DOUBLE_POINTS });
  if (index === -1) {
    return false;
  } else {
    return true;
  }
};

/**
 * Tells you if game coordinate is in the center
 * @param {object} object {s, r, q} i.e. an object with all three game coordinate axes
 * @returns true or false
 */
// export const isCenter = (coords) => {
//   const index = coordInPieces({ coords, piecesArray: CENTER_HEXS });
//   if (index === -1) {
//     return false;
//   } else {
//     return true;
//   }
// };
export const isCenter = (coords) => {
  const q = coords.q;
  const qCenters = [GAME_BOARD_SIZE / 2 - 1, GAME_BOARD_SIZE / 2];
  if (!qCenters.includes(q)) {
    return false; // if the coords aren't the qCenters, get out ofhere
  }
  const r = coords.r;
  var rCenters = [];
  switch (GAME_BOARD_SIZE) {
    case 6:
      rCenters = [1, 2];
      return rCenters.includes(r);
      break;
    case 8:
      const rCentersQLeft = [2, 3];
      const rCentersQRight = [1, 2];
      if (q === qCenters[0]) {
        return rCentersQLeft.includes(r);
      }
      if (q === qCenters[1]) {
        return rCentersQRight.includes(r);
      }
      return false;
      break;
  }
};

/**
 * tells you if two coordinates are the same
 * @param {coord1} coords {s, r, q} i.e. an object with all three game coordinate axes
 * @param {coord2} coords {s, r, q} i.e. an object with all three game coordinate axes
 * @returns boolean e.g. true or false
 */
export const isSame = ({ coords1, coords2 }) => {
  if (!Object.keys(coords1).includes("s")) {
    coords1.s = figureOutS({ q: coords1.q, r: coords1.r });
  }
  if (!Object.keys(coords2).includes("s")) {
    coords2.s = figureOutS({ q: coords2.q, r: coords2.r });
  }

  if (
    coords1.s === coords2.s &&
    coords1.q === coords2.q &&
    coords1.r === coords2.r
  ) {
    return true;
  } else {
    return false;
  }
};

/**
 * Tells you if a game coordinate is in gameState.availables
 * @param {coords} coords {s,q,r}
 * @returns {boolean} true or false
 */
const coordInAvailables = (coords) => {
  for (const availItem of gameState.availables) {
    if (
      availItem.coords.s === coords.s &&
      availItem.coords.q === coords.q &&
      availItem.coords.r === coords.r
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Tells you position of a coordinate in an array of coordinates
 * @param {object} object with two keys: coords and piecesArray
 * @returns {number} index if found or -1 if not
 */
export const coordInPieces = ({ coords, piecesArray }) => {
  let index = 0;
  if (!Array.isArray(piecesArray)) {
    console.log(
      "Error: coordInPiece got called, but input piecesArray is typeof " +
        typeof piecesArray,
    );
    return;
  }
  for (const piece of piecesArray) {
    if (
      piece.coords.s === coords.s &&
      piece.coords.q === coords.q &&
      piece.coords.r === coords.r
    ) {
      return index;
    }
    index++;
  }
  return -1;
};

/**
 * Tells you if a game coordinate could be played on. Results are valid for both the
 * the Push action or Rotate action
 * @param {coords} coords {s, r, q} i.e. an object with all three game coordinate axes
 * @returns {object} The return object always has this for both actions {available: true or false}.
 * available true means you could play there, false means you can't.
 * If it's a "Rotate" action, the it also returns an additional key that is an array of validDirections
 * because you can't rotate to a blocked direction. validDirections might be empty if there are none.
 * If it's a "Push" action, then it also returns an additional key that is the movePlan, which is an
 * array of pieces that would have to be moved. movePlan might be empty if there are none.
 *
 */
export const isAvailable = (coords) => {
  const turn = matchService.getTurn().player;
  const playerAction = matchService.getAction(turn);
  switch (playerAction) {
    case "push":
      const pushResult = isPushAvailable(coords);
      return pushResult;
      break;
    case "rotate":
      const rotateResult = isRotateAvailable({
        coords: coords,
        turnAsPlayer: turn,
      });
      return rotateResult;
      break;
    case "remove":
      const removeResult = isRemoveAvailable({
        coords: coords,
        turnAsPlayer: turn,
      });
      return removeResult;
      break;
  }
  console.log(
    "ERROR: isAvailable returned false because it did could find a valid action: " +
      playerAction,
  );
  console.trace();
  return { available: false };
};

/**
 * Calculates the S axis when you know the other two
 * @param {number} q the q axis
 * @param {number} r the r axis
 * @returns the S axis
 */
export const figureOutS = (q, r) => {
  return -q - r;
};
/**
 * If the two coords { s,q,r} are neighbors, then it will tell
 * you in what direction the toCoords are (from the perspective of the
 * fromCoords.) If they are not neighbors, then it returns "NotANeighbor" which
 * you have to pronounce in your head as if Arnold Schwarzenegger said it.
 * @param {{ fromCoords: coords, toCoords: coords }} coords
 * @returns {string} string that indicates direction OR "NotANeighbor"
 */
export const calculateDirection = ({ fromCoords, toCoords }) => {
  const neighbors = getNeighbors(fromCoords); //array
  const index = coordInPieces({
    coords: toCoords,
    piecesArray: neighbors,
  });
  let direction = matchService.getDirection(matchService.getTurn().player);
  switch (index) {
    case 0:
      direction = "up";
      break;
    case 1:
      direction = "upRight";
      break;
    case 2:
      direction = "downRight";
      break;
    case 3:
      direction = "down";
      break;
    case 4:
      direction = "downLeft";
      break;
    case 5:
      direction = "upLeft";
      break;
  }
  if (index > -1) {
    return direction;
  }
  return "NotANeighbor";
};

export const directionFriendlyName = (direction) => {
  switch (direction) {
    case "up":
      return "Up";
    case "upRight":
      return "Up Right";
    case "downRight":
      return "Down Right";
    case "down":
      return "Down";
    case "downLeft":
      return "Down Left";
    case "upLeft":
      return "Up Left";
  }
};

/**
 * @brief tells you if the remove action is available for given coordinate for a given player
 * @param {{coords:coords, turnAsPlayer:Player}} coords {s, r, q} i.e. an object with all three game coordinate axes
 * @returns {{available: boolean}}} The return object is {available: true or false}
 * available true means you can remove that piece
 * false means you can't
 */

export const isRemoveAvailable = ({ coords, turnAsPlayer }) => {
  const matchResult = matchService.getCurrentMatch();
  if (matchResult.status != 200) {
    // no current match
    return;
  }
  const piece2Remove = gameCoordState(coords);
  if (piece2Remove.player == "Empty") {
    return { available: false };
  }
  const match = matchResult.currentMatch;
  switch (turnAsPlayer) {
    case "playerOne":
      const p1RemovesUsed = match?.p1RemovesUsed ?? 0;
      if (p1RemovesUsed < P1_REMOVES) {
        return { available: true };
      } else {
        return { available: false };
      }
      break;
    case "playerTwo":
      const p2RemovesUsed = match?.p2RemovesUsed ?? 0;
      if (p2RemovesUsed < P2_REMOVES) {
        return { available: true };
      } else {
        return { available: false };
      }
      break;
  }
};

/**
 * @brief Determines if a piece can be rotated and identifies valid directions.
 * @description A rotation is available if the piece belongs to the player and
 * can point in at least one direction that would result in a valid push plan.
 * @param {Object} params - Input parameters.
 * @param {Object} params.coords - Target hex coordinates.
 * @param {string} params.turnAsPlayer - The player attempting the rotation.
 * @returns {Object} { available: boolean, validDirections: string[] }
 */
export const isRotateAvailable = ({ coords, turnAsPlayer }) => {
  const pieceToRotate = gameCoordState(coords);

  // Guard: You can't rotate what you don't own
  if (pieceToRotate.player !== turnAsPlayer) {
    return { available: false, validDirections: [] };
  }

  // 1. Find all directions that result in a valid move plan
  const potentialDirections = calculatePushableDirections(coords);

  // 2. Filter out the direction we are already facing (no point in "rotating" to the same spot)
  const validDirections = potentialDirections.filter(
    (dir) => dir !== pieceToRotate.direction,
  );

  return {
    available: validDirections.length > 0,
    validDirections,
  };
};

/**
 * @brief Checks every possible hex direction to see if a push is physically possible.
 * @param {Object} coords - The coordinates of the piece to test.
 * @returns {string[]} An array of direction strings that are unblocked.
 */
const calculatePushableDirections = (coords) => {
  return DIRECTIONS.filter((direction) => {
    const movePlan = calcMovePlanHypothetical({
      coords2Check: coords,
      direction2Check: direction,
    });

    return movePlan.length > 0;
  });
};

/**
 * @brief Calculates a chain reaction of pieces being pushed.
 * @description Recursively (via iteration) follows the path of pieces pushed
 * by their own facing directions until it hits an empty space or an invalid boundary.
 * @param {Object} params - Input parameters.
 * @param {Object} params.coords2Check - The starting hex coordinates.
 * @param {string} params.direction2Check - The initial push direction.
 * @returns {Array} Array of piece objects to move, or empty array if move is blocked.
 */
const calcMovePlanHypothetical = ({ coords2Check, direction2Check }) => {
  const startHex = gameCoordState(coords2Check);

  if (isHexEmpty(startHex)) {
    return [];
  }

  const context = {
    movePlan: [startHex],
    currentCoords: coords2Check,
    currentDirection: direction2Check,
    isValid: true,
    safetyCheck: 0,
  };

  return executePathCalculation(context);
};

/**
 * @brief Iterates through the push path until a termination state is reached.
 * @param {Object} context - The current state of the path calculation.
 * @returns {Array} The final move plan or an empty array if invalid.
 */
const executePathCalculation = (context) => {
  const MAX_MOVES = 100;
  let keepLooping = true;

  while (keepLooping && context.safetyCheck < MAX_MOVES) {
    context.safetyCheck++;

    const nextNeighbor = getNeighbor({
      coords: context.currentCoords,
      direction: context.currentDirection,
    });

    const action = evaluateNextStep({
      neighbor: nextNeighbor,
      movePlan: context.movePlan,
    });

    switch (action.type) {
      case "CONTINUE":
        context.movePlan.push(nextNeighbor);
        context.currentCoords = nextNeighbor.coords;
        context.currentDirection = nextNeighbor.direction;
        break;

      case "SUCCESS":
        keepLooping = false;
        break;

      case "ABORT":
        context.isValid = false;
        keepLooping = false;
        break;
    }
  }

  if (context.safetyCheck >= MAX_MOVES) {
    console.error("MovePlan exceeded safety limit:", context.movePlan);
    return [];
  }

  return context.isValid ? context.movePlan : [];
};

/**
 * @brief Analyzes the next neighbor to decide if the push chain continues, ends, or fails.
 * @param {Object} params - The neighbor to check and the current plan for collision detection.
 * @returns {Object} An action object with a type: CONTINUE, SUCCESS, or ABORT.
 */
const evaluateNextStep = ({ neighbor, movePlan }) => {
  const isOffBoard = neighbor.coords.s === -99;

  if (isOffBoard) {
    return { type: "ABORT" }; // Edge of board blocks push
  }

  const isCollision =
    coordInPieces({
      coords: neighbor.coords,
      piecesArray: movePlan,
    }) >= 0;

  if (isCollision) {
    return { type: "ABORT" }; // Hit a piece already in the move plan
  }

  if (isHexEmpty(neighbor)) {
    return { type: "SUCCESS" }; // Hit an empty spot, move is valid
  }

  // Otherwise, it's an occupied hex on the board, keep pushing
  return { type: "CONTINUE" };
};

/**
 * @brief Simple utility to check if a hex state is considered empty.
 */
const isHexEmpty = (hexState) => {
  return !hexState || hexState.player === "Empty";
};

/**
 * @brief Predicts the chain reaction of a push starting at a specific hex.
 * @description Extracts the current state of a hex and initiates a recursive
 * calculation to determine which pieces will be displaced.
 * @param {Object} coords - The starting game coordinates {s, r, q}.
 * @returns {Array} An array of piece objects representing the displacement plan.
 */
const calculateMovePlanOfPiece = (coords) => {
  const startHex = gameCoordState(coords);

  // Guard: If there's no piece here, there's nothing to push!
  if (!startHex || !startHex.direction) {
    return [];
  }

  return calcMovePlanHypothetical({
    coords2Check: coords,
    direction2Check: startHex.direction,
  });
};

/**
 * @brief Determines if a push action can be initiated at the given coordinates.
 * @description Validates the hex based on the 'Center Rule' for new games or
 * by calculating a move plan for occupied hexagons.
 * @param {Object} coords - The targeted game coordinates {s, r, q}.
 * @returns {Object} { available: boolean, movePlan: Array }
 */
export const isPushAvailable = (coords) => {
  const pieces = matchService.getPieces().pieces;
  const isTargetCenter = isCenter(coords);

  // Case 1: First move of the game - must be in the center
  if (pieces.length === 0) {
    return {
      available: isTargetCenter,
      movePlan: [],
    };
  }

  const existingPieceIndex = coordInPieces({
    coords,
    piecesArray: pieces,
  });

  // Case 2: Target hex is empty
  if (existingPieceIndex === -1) {
    return {
      available: isTargetCenter,
      movePlan: [],
    };
  }

  // Case 3: Target hex is occupied - check if the piece can be pushed
  return getOccupiedHexStatus(coords);
};

/**
 * @brief Calculates availability for a hex that already contains a piece.
 * @description A push is available only if the piece (and any pieces behind it)
 * can successfully shift out of the way.
 * @param {Object} coords - The coordinates of the occupied hex.
 * @returns {Object} The availability status and the resulting move plan.
 */
const getOccupiedHexStatus = (coords) => {
  const movePlan = calculateMovePlanOfPiece(coords);

  return {
    available: movePlan.length > 0,
    movePlan: movePlan,
  };
};

/**
 * Tells you if a coordinate is on the grid.
 * @param {coords} coords { s, q, r}
 *    +s   +r
 * -q   Hex  +q
 *    -s   +s
 * * s axis (upLeft through downRight, increments up left, decrements down right)
 * * q axis (horizontal axis, 0 is the left most, increments to the right)
 * * r axis (downLeft through upRight, decrements up right, increments down left)
 * @returns {boolean} true or false
 */
export const isOnGrid = (coords) => {
  var onGrid = true;

  // q is the easiest: has to be 0 through 7
  if (coords.q < 0 || coords.q > GAME_BOARD_SIZE - 1) {
    onGrid = false;
  }
  // s <0 is always off grid, so let's check for that
  if (coords.s > 0) {
    onGrid = false;
  }
  // and a list of boundary coords is the only way I could figure out top and bottom edged
  // This will work right up to the point when I change the board size ;-)
  // if the coord is in these boundary coords, then it's offgrid
  const bottom = GAME_BOARD_SIZE === 6 ? BOTTOM_BOUNDARY6 : BOTTOM_BOUNDARY8;
  if (
    coordInPieces({ coords, piecesArray: TOP_BOUNDARY }) >= 0 ||
    coordInPieces({ coords, piecesArray: bottom }) >= 0
  ) {
    onGrid = false;
  }
  return onGrid;
};

/**
 * Tells you the coordinates of a neighbor. If the neighbor
 * is off the grid then it returns { s: -99, q: -99, r: -99 },
 * @param {{ coords: coords, direction:string }} param0
 * @return {{coords:coords}}
 */
export const getNeighborCoords = ({ coords, direction }) => {
  let sOffset = 0;
  let qOffset = 0;
  let rOffset = 0;
  switch (direction) {
    case "up":
      sOffset = 1;
      rOffset = -1;
      break;
    case "upRight":
      qOffset = 1;
      rOffset = -1;
      break;
    case "downRight":
      sOffset = -1;
      qOffset = 1;
      break;
    case "down":
      sOffset = -1;
      rOffset = 1;
      break;
    case "downLeft":
      qOffset = -1;
      rOffset = 1;
      break;
    case "upLeft":
      sOffset = 1;
      qOffset = -1;
      break;
    default:
      console.log(
        "Error: getNeighbor received an invalid direction: " + direction,
      );
      break;
  }
  const neighborCoords = {
    s: coords.s + sOffset,
    q: coords.q + qOffset,
    r: coords.r + rOffset,
  };

  if (!isOnGrid(neighborCoords)) {
    // this means the neighbor is not on the grid
    return { s: -99, q: -99, r: -99 };
  }
  return neighborCoords;
};

/**
 * Tells you the state of a neighboring hexagon
 * @param {object} object {coords, direction} where coords is {s, r, q} and direction is neighbor you want to check
 * @returns {object} piece { player, direction, coords:{s,q,r} } i.e. the piece in the neighboring hexagon.
 * player and direction could be "Empty",
 * or if it's not empty then "player" is "playerOne" or "playerTwo"
 * and "direction" is the way the neighbor is facing.
 * coords is the coordinates of the neighbor, except if it's off the edge. In that case, it returns
 * { player: "Empty", direction: "Empty", coords:{s: -99 ,q: -99 ,r: -99 } }
 */
export const getNeighbor = ({ coords, direction }) => {
  const params = getNeighborCoords({ coords, direction });
  if (!isOnGrid(params)) {
    // this means the neighbor is not on the grid
    return {
      player: "Empty",
      direction: "Empty",
      coords: { s: -99, q: -99, r: -99 },
    };
  }

  const neighborState = gameCoordState(params);
  neighborState.coords = params;
  return neighborState;
};

/**
 * Tells you the neighbors of a coordinate
 * @param {coords} coords {s,q,r}
 * @returns {[pieces]} e.g. array of neighbors, where each item is a piece:
 * Example: {player: "playerOne", direction: "up", coords: {s: -5, q: 3, r: 2}}
 * * player and direction can be "Empty" if there is no piece
 * * coordinates will be -99 if that neighbor is off the grid
 */
export const getNeighbors = (coords) => {
  let neighbors = [];
  neighbors.push(getNeighbor({ coords, direction: "up" }));
  neighbors.push(getNeighbor({ coords, direction: "upRight" }));
  neighbors.push(getNeighbor({ coords, direction: "downRight" }));
  neighbors.push(getNeighbor({ coords, direction: "down" }));
  neighbors.push(getNeighbor({ coords, direction: "downLeft" }));
  neighbors.push(getNeighbor({ coords, direction: "upLeft" }));
  return neighbors;
};

/**
 * Tells you the state of a hexagon
 * @param {coords} coords i.e. an object with all three game coordinate axes
 * @returns {piece} piece { player:, direction, coords: {s,q,r} } i.e. a piece object
 * Case 1: The coords don't have a piece, so player and direction will be returned as "Empty"
 * Case 2: There is a piece. "player" is "playerOne" or "playerTwo" and "direction" is the way its facing
 * and the coords returned are the same coords received in Case 1 and 2.
 * Case 3: The coords are off the board, so it returns this:
 * { player: "Empty", direction: "Empty", coords:{s: -99 ,q: -99 ,r: -99 } }
 */
export const gameCoordState = (coords) => {
  const rewindOffset = gameState.rewindOffset;
  var piece = {
    player: "Empty",
    direction: "Empty",
    coords: coords,
  };

  const currentMatch = matchService.getCurrentMatch().currentMatch;
  let pieces = currentMatch.pieces;

  if (rewindOffset < 0) {
    const moves = currentMatch?.moves;
    if (Array.isArray(moves)) {
      const historicalMove = moves.at(rewindOffset - 1);
      if (historicalMove && historicalMove.pieces) {
        pieces = historicalMove.pieces;
      }
    }
  }

  const index = coordInPieces({ coords, piecesArray: pieces });
  if (index != -1) {
    piece.player = pieces[index].player;
    piece.direction = pieces[index].direction;
  }
  return piece;
};

const compareNumbers = (a, b) => {
  return a - b;
};

const pixelAdj4Padding = ({ x, y }) => {
  //got to adjust for padding and line width
  //canvas padding is 10px, so:
  x = x - 10 - HEX_LINE_WIDTH;
  y = y - 10 - HEX_LINE_WIDTH;
  return { adjX: x, adjY: y };
};

/**
 * Tells you which side of the hexagon is closest to the mouse
 * @param {{coords:coords, mouseX:Integer, mouseY:Integer}} object
 * @returns {string} the side of the hexagon
 */
export const nearestSide = ({ coords, mouseX, mouseY }) => {
  //got to adjust for padding and line width
  const { adjX, adjY } = pixelAdj4Padding({ x: mouseX, y: mouseY });
  const { cx, cy } = gameCoords2Pixels(coords); // get the center point
  const vertices = getHexagonVertices(cx, cy); // [{x,y},{x,y}, ... ]
  const allDeltaXYs = [];
  // get the abs value of the distance from the pointer to each vertex for x and y
  // and add them together
  // and the lowest value is the closest vertex
  for (const vertex of vertices) {
    const deltaX = Math.abs(adjX - vertex.x);
    const deltaY = Math.abs(adjY - vertex.y);
    const deltaXY = deltaX + deltaY;
    allDeltaXYs.push(deltaXY); // array of integers
  }
  const sortedAllDeltaXYs = allDeltaXYs.toSorted(compareNumbers); //returns new array, leaving original
  const nearest = sortedAllDeltaXYs[0];
  const secondNearest = sortedAllDeltaXYs[1];
  const nearestIndex = allDeltaXYs.indexOf(nearest);
  const secondNearestIndex = allDeltaXYs.indexOf(secondNearest);
  switch (true) {
    case (nearestIndex === 0 && secondNearestIndex === 1) ||
      (nearestIndex === 1 && secondNearestIndex === 0):
      return "downRight";
      break;
    case (nearestIndex === 1 && secondNearestIndex === 2) ||
      (nearestIndex === 2 && secondNearestIndex === 1):
      return "down";
      break;
    case (nearestIndex === 2 && secondNearestIndex === 3) ||
      (nearestIndex === 3 && secondNearestIndex === 2):
      return "downLeft";
      break;
    case (nearestIndex === 3 && secondNearestIndex === 4) ||
      (nearestIndex === 4 && secondNearestIndex === 3):
      return "upLeft";
      break;
    case (nearestIndex === 4 && secondNearestIndex === 5) ||
      (nearestIndex === 5 && secondNearestIndex === 4):
      return "up";
      break;
    case (nearestIndex === 5 && secondNearestIndex === 0) ||
      (nearestIndex === 0 && secondNearestIndex === 5):
      return "upRight";
      break;
    default:
      // this happens when the two nearest vertexes are not sequential
      // how does that happen? when you are in the middle, sometimes the opposite
      // vertex is closer than the next sequential in. In that case, what is the
      // user intent? Who knows... Therefore, treat it like a click and return
      // their previously stored direction (i.e. direction of the hover)
      return matchService.getDirection(matchService.getTurn().player);
  }
};

/**
 * Translates game coordinates into canvas coordinates.
 * S axis is optional.
 * @param {coords} coords with two keys, the q axis and r axis are required
 * @returns {object} {cx,cy} i.e. an object with the center pixel of a hexagon in canvas coordinates
 */
export const gameCoords2Pixels = ({ q, r }) => {
  const hexSide = zoom.getHexSide();
  const sqrt3 = Math.sqrt(3);
  let cx;
  let cy;
  cx = (3 / 2) * q;
  cy = (sqrt3 / 2) * q + sqrt3 * r;

  cx = cx * hexSide + hexSide;
  cy = Math.round(cy * hexSide + hexSide);

  return { cx, cy };
};

/**
 * Translates canvas coordinates into the nearest game coordinates
 * @param {cx,cy} object e.g and object with the canvas coordinates
 * @returns {object} {s, r, q} i.e. an object with all three game coordinate axes
 */
export const pixels2gameCoords = ({ cx, cy }) => {
  //got to adjust for padding and line width
  const { adjX, adjY } = pixelAdj4Padding({ x: cx, y: cy });
  const hexagons = gameState.hexagons;
  let nearestTotal = 1000;
  let nearestHexagon = {};
  for (const hexagon of hexagons) {
    const deltaX = Math.abs(adjX - hexagon.cx);
    const deltaY = Math.abs(adjY - hexagon.cy);
    const deltaTotal = deltaX + deltaY;
    if (deltaTotal < nearestTotal) {
      nearestTotal = deltaTotal;
      nearestHexagon = hexagon;
    }
  }
  const gameCoords = {
    s: nearestHexagon.s,
    q: nearestHexagon.q,
    r: nearestHexagon.r,
  };
  return gameCoords;
};

export const getHexagonVertices = (cx, cy) => {
  const vertices = [];
  for (let i = 0; i < 6; i++) {
    const angle_deg = 60 * i;
    const angle_rad = (Math.PI / 180) * angle_deg;
    const x = cx + zoom.getHexSide() * Math.cos(angle_rad);
    const y = cy + zoom.getHexSide() * Math.sin(angle_rad);
    vertices.push({ x, y });
  }
  return vertices;
};

export const drawCenterText = ({ text, coords }) => {
  const canvas = document.getElementById("canvas");
  const ctx = canvas.getContext("2d");
  const canvasCoords = gameCoords2Pixels(coords);
  ctx.font = "normal " + zoom.getHexSide() * 0.4 + "px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.fillStyle = "black";
  ctx.fillText(text, canvasCoords.cx, canvasCoords.cy + 5);
  ctx.stroke();
};

/**
 * tells you who is winning
 * @returns {userID} the userID of the player with the highest score
 */
export const getHighestScore = () => {
  const currentMatch = matchService.getCurrentMatch().currentMatch;
  if (currentMatch.p1Score > currentMatch.p2Score) {
    return currentMatch.p1UserID;
  }
  if (currentMatch.p1Score < currentMatch.p2Score) {
    return currentMatch.p2UserID;
  }
  return "none";
};

export const generateARRs = ({ match, style }) => {
  const moves = match?.moves ?? [];

  let p1Pushes = 0;
  let p1Rotates = 0;
  let p1Removes = 0;
  let p2Pushes = 0;
  let p2Rotates = 0;
  let p2Removes = 0;

  for (const move of moves) {
    switch (true) {
      case match.p1UserID === move.player && move.action === "push":
        p1Pushes++;
        break;
      case match.p1UserID === move.player && move.action === "rotate":
        p1Rotates++;
        break;
      case match.p1UserID === move.player && move.action === "remove":
        p1Removes++;
        break;
      case match.p2UserID === move.player && move.action === "push":
        p2Pushes++;
        break;
      case match.p2UserID === move.player && move.action === "rotate":
        p2Rotates++;
        break;
      case match.p2UserID === move.player && move.action === "remove":
        p2Removes++;
        break;
    }
  }

  let p1ARR;
  let p2ARR;
  switch (style) {
    case "long":
      p1ARR = `<div class="relative">Adds: <span class="courier">${p1Pushes}</span> Rotates: <span class="courier">${p1Rotates}</span> Removes: <span class="courier">${p1Removes}</span><div id="p1ARRSplainer${style}" class="iARRCurrentMatch">ⓘ</div></div>`;
      p2ARR = `<div class="relative">Adds: <span class="courier">${p2Pushes}</span> Rotates: <span class="courier">${p2Rotates}</span> Removes: <span class="courier">${p2Removes}</span><div id="p1ARRSplainer${style}" class="iARRCurrentMatch">ⓘ</div></div>`;
      break;
    case "short":
      p1ARR = `<span class="courier">${p1Pushes}</span>-<span class="courier">${p1Rotates}</span>-<span class="courier">${p1Removes}</span><div id="p1ARRSplainer${style}" class="iARR">ⓘ</div>`;
      p2ARR = `<span class="courier">${p2Pushes}</span>-<span class="courier">${p2Rotates}</span>-<span class="courier">${p2Removes}</span><div id="p1ARRSplainer${style}" class="iARR">ⓘ</div>`;
      break;
  }

  return { p1ARR, p2ARR };
};
